import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton, SvgIcon
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import Tooltip from 'src/components/Tooltip';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ModalDialogVideo from 'src/components/ModalDialogVideo/index';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import ApplyShareCodeForm from 'src/views/Settings/MessageView/ApplyShareCode/ApplyShareCodeForm';
import {
  verifyMessageTemplateShareCode,
  applyMessageTemplateShareCode,
} from 'src/actions/messagesActions';
import helpers from 'src/helpers';
import { PERMISSIONS } from 'src/constants';

import buttonBoxStyle from 'src/views/Settings/MessageView/ButtonBox/buttonBox.style';

function ButtonBox({
  classes,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [verified, setVerified] = useState(null);
  const [verifyError, setVerifyError] = useState('');
  const [loading, setLoading] = useState(false);

  const userRolePermissions = useSelector((state) => state?.account?.user?.userRole?.permissions) || [];
  // CON-1625 — dark by default; granted per-user like SHARE_KEYWORDS.
  const canShareMessageTemplates = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SHARE_MESSAGE_TEMPLATES,
    userRolePermissions,
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const resetShareState = () => {
    setShareCode('');
    setVerified(null);
    setVerifyError('');
    setLoading(false);
  };

  const handleOpenModal = () => {
    resetShareState();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    resetShareState();
  };

  const handleChangeShareCode = (value) => {
    setShareCode(value);
    // A changed code invalidates the previous preview, so Apply cannot fire
    // against a code the user has since edited.
    setVerified(null);
    setVerifyError('');
  };

  const handleVerify = async () => {
    setLoading(true);
    setVerifyError('');
    try {
      const data = await dispatch(verifyMessageTemplateShareCode(shareCode));
      setVerified(data);
    } catch (e) {
      const { error, message } = e?.response?.data || {};
      setVerified(null);
      setVerifyError(error || message || 'Invalid Share Code');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!verified) return;
    await dispatch(applyMessageTemplateShareCode(shareCode));
    handleCloseModal();
  };

  return (
    <>
      <Grid
        className={classes.rootBox}
        container
        spacing={0}
      >
        <Grid>
          <Tooltip
            title="Message Templates"
            placement="left-start"
          >
            <IconButton
              className={classes.buttonVideo}
              color="secondary"
              onClick={handleOpen}
              size="large"
            >
              <SvgIcon fontSize="large">
                <OndemandVideoIcon />
              </SvgIcon>
            </IconButton>
          </Tooltip>
        </Grid>
        {canShareMessageTemplates && (
          <Grid>
            <Tooltip
              title="Apply a Message Template Share Code - Yours or Another User's!"
              placement="right-start"
            >
              <IconButton color="secondary" onClick={handleOpenModal} size="large">
                <SvgIcon fontSize="large">
                  <ShareOutlinedIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
      <ModalDialogVideo
        onClose={handleClose}
        isVisible={open}
        videosrc="https://player.vimeo.com/video/788080622"
        title="Message Templates"
        largeModal
      />
      <ModalDialogMUI6
        onClose={handleCloseModal}
        isVisible={modalOpen}
        content={(
          <ApplyShareCodeForm
            shareCode={shareCode}
            verified={verified}
            verifyError={verifyError}
            loading={loading}
            onChangeShareCode={handleChangeShareCode}
            onVerify={handleVerify}
          />
        )}
        loading={!verified}
        closeText="Cancel"
        okText="Apply"
        onOk={() => {}}
        popConfirmProps={{
          title: 'Are you sure? This will ADD a new Message Template!',
          onConfirm: handleApply,
        }}
        title="Apply Share Code - Import Message Template!"
      />
    </>
  );
}

ButtonBox.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(ButtonBox, buttonBoxStyle, { withTheme: true });
